import React, { useState } from 'react'
import {ClipboardList, Heart, Users, Activity} from 'lucide-react'
import GAD7Assessment from '@/components/assessments/GAD7Assessment'
import PHQ9Assessment from '@/components/assessments/PHQ9Assessment'
import SocialAnxietyAssessment from '@/components/assessments/SocialAnxietyAssessment'
import PanicDisorderAssessment from '@/components/assessments/PanicDisorderAssessment'

type AssessmentType = 'gad7' | 'phq9' | 'social' | 'panic'

interface AssessmentCard {
  id: AssessmentType
  title: string
  subtitle: string
  description: string
  duration: string
  icon: React.ReactNode
  color: string
  bgColor: string
}

export default function AssessmentPage() {
  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentType | null>(null)

  const assessments: AssessmentCard[] = [
    {
      id: 'gad7',
      title: 'GAD-7',
      subtitle: 'Generalized Anxiety Disorder',
      description: 'Screen for generalized anxiety and worry symptoms using the clinically validated GAD-7 scale.',
      duration: '2-3 minutes',
      icon: <ClipboardList className="w-8 h-8" />,
      color: 'text-[#4A90E2]',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'phq9',
      title: 'PHQ-9',
      subtitle: 'Depression Screening',
      description: 'Assess depression symptoms that often accompany anxiety disorders with the PHQ-9 tool.',
      duration: '3-4 minutes',
      icon: <Heart className="w-8 h-8" />,
      color: 'text-[#48C9B0]',
      bgColor: 'bg-teal-50'
    },
    {
      id: 'social',
      title: 'Social Anxiety',
      subtitle: 'Social Phobia Scale',
      description: 'Evaluate social anxiety and fear of social situations with this comprehensive screening.',
      duration: '4-5 minutes',
      icon: <Users className="w-8 h-8" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      id: 'panic',
      title: 'Panic Disorder',
      subtitle: 'Panic Severity Scale',
      description: 'Screen for panic disorder symptoms including panic attacks and avoidance behaviors.',
      duration: '3-4 minutes',
      icon: <Activity className="w-8 h-8" />,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ]

  const renderAssessment = () => {
    switch (selectedAssessment) {
      case 'gad7':
        return <GAD7Assessment />
      case 'phq9':
        return <PHQ9Assessment />
      case 'social':
        return <SocialAnxietyAssessment />
      case 'panic':
        return <PanicDisorderAssessment />
      default:
        return null
    }
  }

  if (selectedAssessment) {
    return (
      <div className="bg-white">
        {/* Back Button */}
        <div className="bg-gradient-to-br from-[#5DADE2] to-[#48C9B0] py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSelectedAssessment(null)}
              className="text-white hover:text-blue-100 transition-colors font-semibold"
            >
              ← Back to All Assessments
            </button>
          </div>
        </div>
        
        {renderAssessment()}
      </div>
    )
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#5DADE2] to-[#48C9B0] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              Mental Health Self-Assessments
            </h1>
            <p className="text-xl text-blue-100">
              Choose from clinically validated screening tools to understand your mental health. These assessments are used by healthcare professionals across the United States.
            </p>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative h-64 md:h-96 overflow-hidden">
        <img
          src="https://static.lumi.new/c4/c44b4158366e87b738ca51f0b9e66e52.png"
          alt="Mental health assessment"
          className="w-full h-full object-cover"
        />
      </section>

      {/* Assessment Selection Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Select an Assessment</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Each assessment is designed to screen for specific mental health conditions. Choose the one that best matches your concerns.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {assessments.map((assessment) => (
              <div
                key={assessment.id}
                className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-[#4A90E2] hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => setSelectedAssessment(assessment.id)}
              >
                <div className={`${assessment.bgColor} w-16 h-16 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <div className={assessment.color}>{assessment.icon}</div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{assessment.title}</h3>
                <p className="text-sm font-semibold text-gray-500 mb-3">{assessment.subtitle}</p>
                <p className="text-gray-700 mb-4">{assessment.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Duration: {assessment.duration}</span>
                  <button className="text-[#4A90E2] font-semibold hover:text-[#3A7BC8] transition-colors">
                    Start Assessment →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Multiple Assessments */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why Multiple Assessments?</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              Mental health conditions often overlap. Approximately <strong>60% of people with anxiety also experience depression</strong>, and specific anxiety subtypes (social anxiety, panic disorder) require targeted screening.
            </p>
            <p className="mb-4">
              Taking multiple assessments provides a comprehensive picture of your mental health and helps identify which treatment approaches may be most beneficial.
            </p>
            <div className="bg-blue-50 border-l-4 border-[#4A90E2] p-6 rounded-r-lg mt-6">
              <p className="text-gray-700 mb-0">
                <strong>Recommended:</strong> If you're unsure which assessment to take, start with the GAD-7 for general anxiety, then consider the PHQ-9 if you also experience low mood or loss of interest in activities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start">
              <ClipboardList className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Important Medical Disclaimer</h4>
                <p className="text-sm text-gray-700">
                  These screening tools are not diagnostic instruments and cannot replace professional evaluation. Only licensed healthcare providers can diagnose mental health conditions. If you're experiencing significant distress or crisis, please contact a mental health professional immediately or call the National Suicide Prevention Lifeline at 988.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legacy Results Section (removed) */}
      {false && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6 print:hidden">
              <button
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF}
                className="inline-flex items-center justify-center px-6 py-3 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF Report
                  </>
                )}
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#4A90E2] border-2 border-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-md"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Report
              </button>
            </div>
            
            <div ref={resultsRef} className={`${interpretation.bgColor} border-2 ${interpretation.borderColor} rounded-xl p-8`}>
              <div className="text-center mb-6">
                <div className="text-6xl font-bold mb-2 text-gray-900">{score}</div>
                <div className={`text-2xl font-bold ${interpretation.color}`}>{interpretation.level}</div>
              </div>
              <div className="bg-white rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Your Results</h3>
                <p className="text-gray-700 mb-4">{interpretation.description}</p>
                <p className="text-gray-700"><strong>Recommendation:</strong> {interpretation.recommendation}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
                <Link
                  to="/therapy"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all"
                >
                  Find a Therapist
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <button
                  onClick={() => {
                    setAnswers({})
                    setShowResults(false)
                  }}
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#4A90E2] border-2 border-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all"
                >
                  Retake Assessment
                </button>
              </div>
              
              {/* Print-only footer */}
              <div className="hidden print:block mt-8 pt-6 border-t border-gray-300">
                <p className="text-sm text-gray-600 text-center">
                  Generated by Anxiety USA - anxietyusa.com | Date: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
